import { Building, Home, LogIn, Settings, Ticket, UserPlus, Users } from "lucide-react"
import type React from "react"
// Page Components
import { DashboardPage } from "@/pages/dashboard/DashboardPage"
import { EchoesConsentPage } from "@/pages/echoes/consent/EchoesConsentPage"
import { NewTenantRegisterPage } from "@/pages/new-tenant-register/NewTenantRegisterPage"
import { PasswordIssuePage } from "@/pages/password-issue/PasswordIssuePage"
import { PasswordResetPage } from "@/pages/password-reset/PasswordResetPage"
import { CompleteProfilePage } from "@/pages/profile/complete/CompleteProfilePage"
import { ChangeEmailPage } from "@/pages/profile/edit/change-email/ChangeEmailPage"
import { ChangePasswordPage } from "@/pages/profile/edit/change-password/ChangePasswordPage"
import { EditProfilePage } from "@/pages/profile/edit/EditProfilePage"
import { OperationalRuleHistoryPage } from "@/pages/profile/operational-rule/history/OperationalRuleHistoryPage"
import { OperationalRulePage } from "@/pages/profile/operational-rule/OperationalRulePage"
import { ProfilePage } from "@/pages/profile/ProfilePage"
import { SubscriptionsPage } from "@/pages/profile/subscriptions/SubscriptionsPage"
import { PropertyDetailPage } from "@/pages/properties/id/PropertyDetailPage"
import { PropertyEditPage } from "@/pages/properties/id/PropertyEditPage"
import { RoomAddPage } from "@/pages/properties/id/RoomAddPage"
import { PlanAddPage } from "@/pages/properties/id/rooms/id/PlanAddPage"
import { PlanCancelPage } from "@/pages/properties/id/rooms/id/plans/id/PlanCancelPage"
import { RoomDetailPage } from "@/pages/properties/id/rooms/id/RoomDetailPage"
import { RoomEditPage } from "@/pages/properties/id/rooms/id/RoomEditPage"
import { TenantAddPage } from "@/pages/properties/id/rooms/id/TenantAddPage"
import { TenantsPage } from "@/pages/properties/id/rooms/id/TenantsPage"
import { TenantCancelPage } from "@/pages/properties/id/rooms/id/tenants/id/TenantCancelPage"
import { TenantDetailPage } from "@/pages/properties/id/rooms/id/tenants/id/TenantDetailPage"
import { TenantEditPage } from "@/pages/properties/id/rooms/id/tenants/id/TenantEditPage"
import { PropertiesPage } from "@/pages/properties/PropertiesPage"
import { PropertyAddPage } from "@/pages/properties/PropertyAddPage"
import { SigninPage } from "@/pages/signin/SigninPage"
import { SignoutPage } from "@/pages/signout/SignoutPage"
import { SignupPage } from "@/pages/signup/SignupPage"
import { TestIndexPage } from "@/pages/TestIndexPage"
import { AddTicketPage } from "@/pages/tickets/add/AddTicketPage"
import { TicketDetailPage } from "@/pages/tickets/TicketDetailPage"
import { TicketsPage } from "@/pages/tickets/TicketsPage"

// Development status constants
export const DevStatus = {
  1: "未対応",
  2: "対応中",
  3: "対応済み",
} as const

export type DevStatusKey = keyof typeof DevStatus
export type DevStatusValue = (typeof DevStatus)[DevStatusKey]

export interface RouteInfo {
  name: string
  path: string
  component?: React.ComponentType
  external?: boolean
  viewStatus?: DevStatusKey
  apiStatus?: DevStatusKey
}

export interface RouteCategory {
  category: string
  icon: React.ComponentType
  routes: RouteInfo[]
}

// 認証不要なページ
export const publicRoutes: RouteCategory[] = [
  {
    category: "認証",
    icon: LogIn,
    routes: [
      {
        name: "サインイン",
        path: "/signin",
        component: SigninPage,
        viewStatus: 3,
        apiStatus: 2,
      },
      {
        name: "サインアップ",
        path: "/signup",
        component: SignupPage,
        viewStatus: 3,
        apiStatus: 3,
      },
      {
        name: "サインアウト",
        path: "/signout",
        component: SignoutPage,
        viewStatus: 3,
        apiStatus: 3,
      },
    ],
  },
  {
    category: "パスワード管理",
    icon: Settings,
    routes: [
      {
        name: "パスワード再発行",
        path: "/password-issue",
        component: PasswordIssuePage,
        viewStatus: 1,
        apiStatus: 1,
      },
      {
        name: "パスワードリセット",
        path: "/password-reset",
        component: PasswordResetPage,
        viewStatus: 1,
        apiStatus: 1,
      },
    ],
  },
  {
    category: "新規入居者登録",
    icon: UserPlus,
    routes: [
      {
        name: "新規入居者登録",
        path: "/new-tenant-register",
        component: NewTenantRegisterPage,
        viewStatus: 1,
        apiStatus: 1,
      },
    ],
  },
]

// 開発用のルート
export const devRoutes: RouteCategory[] = [
  {
    category: "開発用",
    icon: Settings,
    routes: [
      {
        name: "ルート一覧",
        path: "/_list",
        component: TestIndexPage,
        viewStatus: 1,
        apiStatus: 1,
      },
    ],
  },
]

// 認証が必要なページ
export const protectedRoutes: RouteCategory[] = [
  {
    category: "メイン機能",
    icon: Home,
    routes: [
      {
        name: "ホーム",
        path: "/",
        component: DashboardPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "Echoes連携同意",
        path: "/echoes/consent",
        component: EchoesConsentPage,
        viewStatus: 3,
        apiStatus: 2,
      },
    ],
  },
  {
    category: "物件管理",
    icon: Building,
    routes: [
      {
        name: "物件一覧",
        path: "/properties",
        component: PropertiesPage,
        viewStatus: 3,
        apiStatus: 3,
      },
      {
        name: "物件登録",
        path: "/properties/add",
        component: PropertyAddPage,
        viewStatus: 3,
        apiStatus: 3,
      },
      {
        name: "物件詳細",
        path: "/properties/:propertyId",
        component: PropertyDetailPage,
        viewStatus: 3,
        apiStatus: 2,
      },
      {
        name: "物件編集",
        path: "/properties/:propertyId/edit",
        component: PropertyEditPage,
        viewStatus: 3,
        apiStatus: 3,
      },
      {
        name: "部屋追加",
        path: "/properties/:propertyId/addRoom",
        component: RoomAddPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "部屋編集",
        path: "/properties/:propertyId/rooms/:roomId/edit",
        component: RoomEditPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "部屋詳細",
        path: "/properties/:propertyId/rooms/:roomId",
        component: RoomDetailPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "入居者一覧",
        path: "/properties/:propertyId/rooms/:roomId/tenants",
        component: TenantsPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "入居者追加",
        path: "/properties/:propertyId/rooms/:roomId/tenants/add",
        component: TenantAddPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "入居者編集",
        path: "/properties/:propertyId/rooms/:roomId/tenants/:tenantId/edit",
        component: TenantEditPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "入居者詳細",
        path: "/properties/:propertyId/rooms/:roomId/tenants/:tenantId",
        component: TenantDetailPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "入居者退去",
        path: "/properties/:propertyId/rooms/:roomId/tenants/:tenantId/cancel",
        component: TenantCancelPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "プラン加入",
        path: "/properties/:propertyId/rooms/:roomId/plans/addPlan",
        component: PlanAddPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "プラン終了",
        path: "/properties/:propertyId/rooms/:roomId/plans/:planId/cancel",
        component: PlanCancelPage,
        viewStatus: 1,
        apiStatus: 1,
      },
    ],
  },
  {
    category: "チケット",
    icon: Ticket,
    routes: [
      {
        name: "チケット一覧",
        path: "/tickets",
        component: TicketsPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "チケット詳細",
        path: "/tickets/:ticketId",
        component: TicketDetailPage,
        viewStatus: 1,
        apiStatus: 1,
      },
      {
        name: "チケット追加",
        path: "/tickets/add",
        component: AddTicketPage,
        viewStatus: 1,
        apiStatus: 1,
      },
    ],
  },
  {
    category: "プロフィール",
    icon: Users,
    routes: [
      {
        name: "プロフィール",
        path: "/profile",
        component: ProfilePage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "プロフィール登録",
        path: "/profile/complete",
        component: CompleteProfilePage,
        viewStatus: 3,
        apiStatus: 3,
      },
      {
        name: "プロフィール編集",
        path: "/profile/edit",
        component: EditProfilePage,
        viewStatus: 1,
        apiStatus: 1,
      },
      {
        name: "メールアドレス変更",
        path: "/profile/edit/change-email",
        component: ChangeEmailPage,
        viewStatus: 1,
        apiStatus: 1,
      },
      {
        name: "パスワード変更",
        path: "/profile/edit/change-password",
        component: ChangePasswordPage,
        viewStatus: 1,
        apiStatus: 1,
      },
      {
        name: "運用ルール設定",
        path: "/profile/operational-rule",
        component: OperationalRulePage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "運用ルール履歴",
        path: "/profile/operational-rule/history",
        component: OperationalRuleHistoryPage,
        viewStatus: 3,
        apiStatus: 1,
      },
      {
        name: "サービス利用状況",
        path: "/profile/subscriptions",
        component: SubscriptionsPage,
        viewStatus: 1,
        apiStatus: 1,
      },
    ],
  },
]

// Extract all paths for route configuration
export const routePaths = {
  // Public routes
  SIGNIN: "/signin",
  SIGNUP: "/signup",
  SIGNUP_REGISTER: "/signup/register",
  SIGNOUT: "/signout",
  LIST: "/_list",

  // Protected routes
  DASHBOARD: "/",
  PASSWORD_ISSUE: "/password-issue",
  PASSWORD_RESET: "/password-reset",

  // Properties
  PROPERTIES: "/properties",
  ADD_PROPERTY: "/properties/add",
  PROPERTY_DETAIL: "/properties/:propertyId",
  EDIT_PROPERTY: "/properties/:propertyId/edit",
  ROOM_DETAIL: "/properties/:propertyId/rooms/:roomId",
  ADD_TENANT: "/properties/:propertyId/rooms/:roomId/tenants/add",
  EDIT_TENANT: "/properties/:propertyId/rooms/:roomId/tenants/:tenantId/edit",
  TENANTS: "/properties/:propertyId/rooms/:roomId/tenants",
  TENANT_DETAIL: "/properties/:propertyId/rooms/:roomId/tenants/:tenantId",
  TENANT_FILES: "/properties/:propertyId/rooms/:roomId/tenants/:tenantId/files",

  // Tickets
  TICKETS: "/tickets",
  TICKET_DETAIL: "/tickets/:ticketId",
  ADD_TICKET: "/tickets/add",

  // Profile
  PROFILE: "/profile",
  COMPLETE_PROFILE: "/profile/complete",
  EDIT_PROFILE: "/profile/edit",
  CHANGE_EMAIL: "/profile/edit/change-email",
  CHANGE_PASSWORD: "/profile/edit/change-password",
  OPERATIONAL_RULE: "/profile/operational-rule",
  OPERATIONAL_RULE_HISTORY: "/profile/operational-rule/history",
  SUBSCRIPTIONS: "/profile/subscriptions",

  // Echoes
  ECHOES_CONSENT: "/echoes/consent",

  // Other
  NEW_TENANT_REGISTER: "/new-tenant-register",

  ROOT: "/",
} as const
